import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const StorageManagement = () => {
  const [storageInfo, setStorageInfo] = useState({
    quota_mb: 0,
    used_mb: 0,
    available_mb: 0
  });
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchStorageData();
  }, []);

  const fetchStorageData = async () => {
    try {
      setLoading(true);
      const [infoRes, filesRes] = await Promise.all([
        adminAPI.getStorageInfo(),
        adminAPI.getStorageFiles({ limit: 100 })
      ]);

      const data = infoRes.data;
      // Compute available_mb if not provided by API (it isn't in your response)
      const available_mb = data.available_mb !== undefined
        ? data.available_mb
        : (data.quota_mb || 0) - (data.used_mb || 0);

      setStorageInfo({
        quota_mb: data.quota_mb || 0,
        used_mb: data.used_mb || 0,
        available_mb: available_mb
      });
      setFiles(filesRes.data);
    } catch (error) {
      toast.error('Failed to load storage data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (fileId, fileName) => {
    if (!window.confirm(`Are you sure you want to delete "${fileName}"? This action cannot be undone.`)) {
      return;
    }
    try {
      setDeleting(true);
      await adminAPI.deleteStorageFile(fileId);
      toast.success(`"${fileName}" deleted successfully`);
      // Refresh data
      await fetchStorageData();
    } catch (error) {
      toast.error('Failed to delete file');
      console.error(error);
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (isoString) => {
    try {
      return format(new Date(isoString), 'dd MMM yyyy, hh:mm a');
    } catch {
      return isoString;
    }
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.startsWith('image/')) return 'fa-image';
    if (mimeType === 'application/pdf') return 'fa-file-pdf';
    if (mimeType?.startsWith('application/vnd.ms-excel') || mimeType?.includes('spreadsheet')) return 'fa-file-excel';
    if (mimeType?.startsWith('text/')) return 'fa-file-alt';
    return 'fa-file';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  const usagePercent = storageInfo.quota_mb > 0
    ? (storageInfo.used_mb / storageInfo.quota_mb) * 100
    : 0;
  const isNearFull = usagePercent > 80;
  const isCritical = usagePercent > 95;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-800 rounded-xl p-6 text-white">
        <h1 className="text-2xl font-bold">Storage Management</h1>
        <p className="opacity-90 mt-1">Manage uploaded files and monitor school storage usage.</p>
      </div>

      {/* Storage Usage Card */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Storage Usage</h3>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex-1 w-full">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600">Used: {storageInfo.used_mb.toFixed(2)} MB</span>
              <span className="text-gray-600">of {storageInfo.quota_mb.toFixed(2)} MB</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4">
              <div
                className={`h-4 rounded-full transition-all duration-300 ${
                  isCritical ? 'bg-red-600' : isNearFull ? 'bg-yellow-500' : 'bg-blue-600'
                }`}
                style={{ width: `${Math.min(usagePercent, 100)}%` }}
              ></div>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Available: {storageInfo.available_mb.toFixed(2)} MB
            </p>
          </div>
          <div className="text-center md:text-right">
            <span className={`text-sm font-medium px-2 py-1 rounded-full ${
              isCritical ? 'bg-red-100 text-red-800' : isNearFull ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
            }`}>
              {usagePercent.toFixed(1)}% used
            </span>
          </div>
        </div>
      </div>

      {/* Files List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-800">Uploaded Files</h3>
          <span className="text-sm text-gray-500">{files.length} file(s)</span>
        </div>
        {files.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <i className="fas fa-cloud-upload-alt text-4xl mb-3 opacity-40"></i>
            <p>No files uploaded yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Uploaded</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {files.map((file) => (
                  <tr key={file.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <i className={`fas ${getFileIcon(file.mime_type)} text-gray-400 mr-3`}></i>
                        <span className="text-sm font-medium text-gray-900">{file.filename}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {file.size_mb} MB
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(file.uploaded_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleDelete(file.id, file.filename)}
                        disabled={deleting}
                        className="text-red-600 hover:text-red-900 disabled:opacity-50"
                      >
                        <i className="fas fa-trash-alt mr-1"></i> Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Note about storage */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
        <div className="flex">
          <div className="flex-shrink-0">
            <i className="fas fa-info-circle text-blue-400"></i>
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              Files uploaded via student passport uploads are stored here. Delete old or unnecessary files to free up space.
              Your school has a quota of <strong>{storageInfo.quota_mb.toFixed(2)} MB</strong>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StorageManagement;